// Builder-specific types
// These extend the shared-types from packages/shared-types

import type { Block } from '@realsite/blocks';

export type PlanTier = 'free' | 'starter' | 'pro' | 'agency';
export type TenantRole = 'owner' | 'admin' | 'editor' | 'viewer' | 'member';

export type ComponentType =
  | 'hero'
  | 'listing_search'
  | 'listing_grid'
  | 'listing_map'
  | 'agent_bio'
  | 'contact_form'
  | 'testimonials'
  | 'text_block'
  | 'image_gallery'
  | 'market_stats'
  | 'cta_banner'
  | 'neighborhood_guide';

export interface Component {
  id: string;
  type: ComponentType;
  props: Record<string, unknown>;
  order: number;
}

export interface NavItem {
  label: string;
  url: string;
  page_id?: string;
  /** Stable id (from GET /site-config). Needed to nest other items UNDER this one. */
  id?: string;
  /** Set when this item is a dropdown child — points at its parent item's `id`.
   *  The live header (<Navigation>) groups children by parent_id into dropdowns. */
  parent_id?: string | null;
  open_in_new_tab?: boolean;
  sort_order?: number;
  /** Which menu this item renders in — 'header' (default) or 'footer'. */
  location?: 'header' | 'footer';
}

export interface FooterConfig {
  tagline?: string;
  show_social?: boolean;
  social_links?: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    twitter?: string;
    youtube?: string;
  };
  columns?: Array<{
    heading: string;
    links: Array<{ label: string; url: string }>;
  }>;
}

export interface Page {
  id: string;
  slug: string;
  title: string;
  /** Gateway's site-config response uses `name`; older builder code uses `title`. Both kept for compat. */
  name?: string;
  meta_description?: string;
  html?: string | null;
  /** Wave 5 Phase D — canonical JSON blocks. Absent/null falls back to html. */
  blocks?: Block[] | null;
  /** Phase F (studio) — the DRAFT working copy of blocks; promoted to `blocks`
   *  (published) on Publish. The tenant render only ever reads `blocks`. */
  draft_blocks?: Block[] | null;
  /** Row `updated_at` — optimistic-concurrency etag the block editor sends as
   *  `expectedUpdatedAt` on save (refuse-don't-clobber a concurrent writer). */
  updated_at?: string;
  is_home_page?: boolean | number;
  components: Component[];
}

export interface SiteStyle {
  color_primary: string;
  color_secondary: string;
  color_accent: string;
  color_background: string;
  color_surface: string;
  color_text: string;
  color_text_muted: string;
  heading_font: string;
  body_font: string;
  content_width: string;
  section_spacing: string;
  border_radius: string;
  hero_image_url?: string | null;
  logo_url?: string;
  favicon_url?: string;
}

/**
 * @deprecated Use SiteStyle. Kept for backward-compat with legacy builder
 * components that have not yet migrated to the new schema.
 */
export interface SiteTheme {
  primary_color: string;
  secondary_color: string;
  font_heading: string;
  font_body: string;
  logo_url?: string;
  favicon_url?: string;
}

export interface SiteConfig {
  tenant_id: string;
  template?: string | null;
  /** New SiteConfig schema — matches packages/site-config SiteConfigStyle */
  style: SiteStyle;
  /** @deprecated Use style. Legacy field kept until all builder components migrate. */
  theme: SiteTheme;
  pages: Page[];
  navigation: NavItem[];
  footer: FooterConfig;
  content?: Record<string, unknown>;
  /**
   * Server-resolved brand identity. GET /site-config populates `brand.logo_url` from
   * site_style_settings.logo_url → brands.logo_primary_url (the same durable logo the live
   * tenant <Navigation> renders), so the studio header preview can lead with it. Optional —
   * present at runtime from the API even though the legacy blob shape didn't declare it.
   */
  brand?: {
    logo_url?: string | null;
    logo_dark_url?: string | null;
    company_name?: string | null;
    /** Footer social links — Brand Manager is the source of truth (see Footer derivation). */
    social?: { instagram?: string | null; facebook?: string | null; linkedin?: string | null; youtube?: string | null };
  };
  /**
   * Hero media for the home look's hero — the agent's own photo / collage / carousel / video,
   * which OVERRIDES the look preset's placeholder at render time (apps/tenant-sites
   * `applyHeroMedia`, and the Studio preview). Set by the Hero Media panel, the site scaffold
   * and the chat's media placement. Mirrors the tenant-sites SiteConfig field of the same name.
   */
  hero_media?: {
    type?: 'image' | 'collage' | 'carousel' | 'video';
    images?: string[];
    videoUrl?: string | null;
    posterUrl?: string | null;
  } | null;
}

// Component-specific prop types
export interface HeroProps {
  headline: string;
  subheadline: string;
  cta_text: string;
  cta_url: string;
  background_image?: string;
  overlay_opacity: number;
  show_search_bar: boolean;
}

export interface ListingSearchProps {
  default_location: string;
  filters: {
    show_price: boolean;
    show_beds: boolean;
    show_type: boolean;
    min_price?: number;
    max_price?: number;
    min_beds?: number;
  };
  layout: 'bar' | 'panel';
}

export interface ListingGridProps {
  title: string;
  subtitle: string;
  filter_by: 'status' | 'type' | 'featured';
  filter_value?: string;
  max_count: number;
  columns: 2 | 3 | 4;
}

export interface ListingMapProps {
  center_lat: number;
  center_lng: number;
  zoom: number;
  cluster_enabled: boolean;
}

export interface SocialLinks {
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  twitter?: string;
  youtube?: string;
}

export interface AgentBioProps {
  photo_url?: string;
  name: string;
  title: string;
  bio: string;
  phone: string;
  email: string;
  license_number?: string;
  social_links?: SocialLinks;
}

export interface ContactFormProps {
  heading: string;
  fields: {
    show_phone: boolean;
    show_message: boolean;
    show_address: boolean;
    show_budget: boolean;
  };
  success_message: string;
  redirect_url?: string;
}

export interface Testimonial {
  id: string;
  name: string;
  text: string;
  rating: number;
  photo_url?: string;
}

export interface TestimonialsProps {
  testimonials: Testimonial[];
}

export interface TextBlockProps {
  content: string;
  alignment: 'left' | 'center' | 'right';
  max_width: 'sm' | 'md' | 'lg' | 'full';
}

export interface ImageGalleryProps {
  images: string[];
  layout: 'grid' | 'masonry' | 'carousel';
}

export interface MarketStatsProps {
  location: string;
  stats_to_show: {
    median_price: boolean;
    days_on_market: boolean;
    list_to_sale: boolean;
    active_listings: boolean;
  };
}

export interface CtaBannerProps {
  headline: string;
  button_text: string;
  button_url: string;
  background_color: string;
}

export interface Neighborhood {
  id: string;
  name: string;
  description: string;
  image_url?: string;
  link?: string;
}

export interface NeighborhoodGuideProps {
  neighborhoods: Neighborhood[];
}

// Lead type
export type LeadStatus =
  | 'new'
  | 'contacted'
  | 'qualified'
  | 'showing'
  | 'offer'
  | 'closed'
  | 'lost';

export type ActivityType = 'note' | 'call' | 'email' | 'showing' | 'status_change';

export interface LeadActivity {
  id: string;
  lead_id: string;
  activity_type: ActivityType;
  description: string;
  agent_name?: string;
  created_at: string;
}

export interface Lead {
  id: string;
  tenant_id: string;
  name: string;
  email: string;
  phone?: string;
  source: string;
  page_slug?: string;
  message?: string;
  status: LeadStatus;
  budget?: string;
  timeline?: string;
  listing_id?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
}

export interface LeadDetail extends Lead {
  activities: LeadActivity[];
}

// Auth
export interface JWTPayload {
  sub: string;
  tenant_id: string;
  tenant_slug: string;
  role: TenantRole;
  plan: PlanTier;
  /** System-level role: 'user' | 'superadmin' */
  user_role?: string;
  iat: number;
  exp: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  company_name: string;
  slug: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token?: string;
  tenant_id: string;
  user: {
    id: string;
    email: string;
  };
  tenant: {
    id: string;
    slug: string;
    plan: string;
  };
}

// API
export interface APIResponse<T> {
  data: T;
  error?: string;
  request_id: string;
}

export interface AIResponse {
  result: string;
  tokens: {
    input: number;
    output: number;
  };
  cost_usd: number;
  feature: string;
}

// Tenant settings
export interface TenantSettings {
  tenant_id: string;
  settings: {
    mapbox_api_key?: string;
    mls_feed_id?: string;
    branding?: {
      primary_color?: string;
      logo_url?: string;
      company_name?: string;
    };
    contact_info?: {
      phone?: string;
      email?: string;
      address?: string;
    };
    integrations?: {
      twilio_number?: string;
      mandrill_sender?: string;
    };
    [key: string]: unknown;
  };
}

export interface TenantDomain {
  id: string;
  tenant_id: string;
  domain: string;
  verified: boolean;
  ssl_status: 'pending' | 'active' | 'failed';
  status?: 'verifying' | 'setting_up' | 'ready' | 'failed';
  created_at: string;
  purchased_via_platform?: boolean;
  purchase_price_usd?: number | null;
  renewal_date?: string | null;
  registrar_domain_id?: string | null;
  cloudflare_hostname_id?: string | null;
  is_primary?: boolean;
  dns_instructions?: {
    cname: {
      type: 'CNAME';
      host: string;
      value: string;
      description: string;
    };
    apex: {
      type: 'A';
      host: string;
      value: string;
      description: string;
    };
    txt: {
      type: 'TXT';
      host: string;
      value: string;
      description: string;
    };
  };
}

export interface TeamMember {
  id: string;
  user_id: string | null;
  email: string;
  role: TenantRole;
  status: 'invited' | 'active' | 'disabled';
  created_at: string;
  /** @deprecated use created_at */
  joined_at?: string;
}

export interface TeamInvite {
  email: string;
  role: 'admin' | 'editor' | 'viewer';
}

export interface InviteResult {
  invited: boolean;
  invite_link: string;
  email: string;
}

// Analytics
export interface AnalyticsSummary {
  visits_30d: number;
  leads_30d: number;
  top_pages: Array<{
    slug: string;
    title: string;
    views: number;
  }>;
}

// Plan limits
export const PLAN_LIMITS = {
  free: {
    // Home + About. The free tier is meant to reach a real, sendable site rather than a teaser:
    // two content pages plus the legal pages, which are counted separately (they live in
    // site_legal_document_settings, not in siteConfig.pages) and are free on every plan because
    // a site without them has broken footer links.
    pages: 2,
    ai_writes_per_month: 0,
    custom_domain: false,
    team_members: 1,
    listing_map: false,
    // Featured communities are a Pro+ feature; free/starter can't feature any.
    max_communities: 0,
  },
  starter: {
    pages: 3,
    ai_writes_per_month: 10,
    custom_domain: true,
    team_members: 2,
    listing_map: false,
    max_communities: 0,
  },
  pro: {
    pages: Infinity,
    ai_writes_per_month: 100,
    custom_domain: true,
    team_members: 5,
    listing_map: true,
    max_communities: 10,
  },
  agency: {
    pages: Infinity,
    ai_writes_per_month: Infinity,
    custom_domain: true,
    team_members: Infinity,
    listing_map: true,
    max_communities: 25,
  },
} as const;

/** Featured-community cap for a plan (0 if the plan can't feature communities). */
export function maxCommunitiesForPlan(plan: string | null | undefined): number {
  const tier = (plan ?? 'free') as keyof typeof PLAN_LIMITS;
  return PLAN_LIMITS[tier]?.max_communities ?? 0;
}

export const COMPONENT_LABELS: Record<ComponentType, string> = {
  hero: 'Hero Section',
  listing_search: 'Listing Search',
  listing_grid: 'Listing Grid',
  listing_map: 'Listing Map',
  agent_bio: 'Agent Bio',
  contact_form: 'Contact Form',
  testimonials: 'Testimonials',
  text_block: 'Text Block',
  image_gallery: 'Image Gallery',
  market_stats: 'Market Stats',
  cta_banner: 'CTA Banner',
  neighborhood_guide: 'Neighborhood Guide',
};

export const DEFAULT_COMPONENT_PROPS: Record<ComponentType, Record<string, unknown>> = {
  hero: {
    headline: 'Find Your Dream Home',
    subheadline: 'Expert real estate services in your area. Browse listings and connect with a local expert.',
    cta_text: 'Browse Listings',
    cta_url: '/listings',
    background_image: '',
    overlay_opacity: 0.5,
    show_search_bar: true,
  },
  listing_search: {
    default_location: '',
    filters: {
      show_price: true,
      show_beds: true,
      show_type: true,
    },
    layout: 'bar',
  },
  listing_grid: {
    title: 'Featured Listings',
    subtitle: 'Browse our latest properties',
    filter_by: 'featured',
    max_count: 6,
    columns: 3,
  },
  listing_map: {
    center_lat: 40.7128,
    center_lng: -74.006,
    zoom: 12,
    cluster_enabled: true,
  },
  agent_bio: {
    photo_url: '',
    name: 'Your Name',
    title: 'Real Estate Agent',
    bio: 'With years of experience in the local market, I help buyers and sellers achieve their real estate goals.',
    phone: '(555) 000-0000',
    email: 'agent@example.com',
    license_number: '',
    social_links: {},
  },
  contact_form: {
    heading: 'Get In Touch',
    fields: {
      show_phone: true,
      show_message: true,
      show_address: false,
      show_budget: false,
    },
    success_message: "Thank you! We'll be in touch shortly.",
    redirect_url: '',
  },
  testimonials: {
    testimonials: [
      {
        id: '1',
        name: 'Sarah Johnson',
        text: 'Outstanding service! Found our dream home in just two weeks.',
        rating: 5,
        photo_url: '',
      },
    ],
  },
  text_block: {
    content: '<p>Add your content here. This is a rich text block where you can write anything you want.</p>',
    alignment: 'left',
    max_width: 'lg',
  },
  image_gallery: {
    images: [],
    layout: 'grid',
  },
  market_stats: {
    location: 'Your City, State',
    stats_to_show: {
      median_price: true,
      days_on_market: true,
      list_to_sale: true,
      active_listings: true,
    },
  },
  cta_banner: {
    headline: 'Ready to find your perfect home?',
    button_text: 'Contact Us Today',
    button_url: '/contact',
    background_color: '#4E1F60',
  },
  neighborhood_guide: {
    neighborhoods: [
      {
        id: '1',
        name: 'Downtown',
        description: 'The heart of the city with vibrant dining, entertainment, and culture.',
        image_url: '',
        link: '',
      },
    ],
  },
};
